<template>
  <div v-if="origen && destino" class="uber-card">
    <div class="uber-header">
      <span class="uber-brand">Uber</span>
      <span class="uber-subtitle">— otra opción</span>
    </div>

    <div v-if="cargando" class="uber-loading">
      <i class="pi pi-spin pi-spinner" />
      <span>Obteniendo direcciones...</span>
    </div>

    <template v-else>
      <div class="uber-field-group">
        <div class="uber-field-label">
          <i class="uber-dot uber-dot--origin" />
          Origen
        </div>
        <input v-model="origenLine1" class="uber-input" placeholder="Calle y número" />
        <input v-model="origenLine2" class="uber-input" placeholder="Ciudad" />
      </div>

      <div class="uber-field-group">
        <div class="uber-field-label">
          <i class="uber-dot uber-dot--dest" />
          Destino
        </div>
        <input v-model="destinoLine1" class="uber-input" placeholder="Calle y número" />
        <input v-model="destinoLine2" class="uber-input" placeholder="Ciudad" />
      </div>
    </template>

    <a
      :href="link"
      target="_blank"
      rel="noopener noreferrer"
      class="uber-link"
    >
      <i class="pi pi-external-link" />
      Abrir en Uber
    </a>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { buildUberLink } from '@/composables/useTransporteUber'
import { useNominatim } from '@/composables/useNominatim'

const props = defineProps({
  origen: Object,
  destino: Object,
})

const { reverseGeocode } = useNominatim()

const origenLine1 = ref('')
const origenLine2 = ref('')
const destinoLine1 = ref('')
const destinoLine2 = ref('')
const cargando = ref(false)

const formatAddr = (data) => {
  if (!data?.address) return { line1: '', line2: '' }
  const a = data.address
  const line1 = a.house_number
    ? `${a.road} ${a.house_number}`.trim()
    : (data.intersection || a.road || data.display_name?.split(',')[0] || '')
  const line2 = a.city || a.town || a.village || a.suburb || ''
  return { line1, line2 }
}

const geocodificar = async () => {
  if (!props.origen || !props.destino) return
  cargando.value = true
  try {
    const [ro, rd] = await Promise.all([
      reverseGeocode(props.origen),
      reverseGeocode(props.destino),
    ])
    const oa = formatAddr(ro)
    const da = formatAddr(rd)
    origenLine1.value = oa.line1
    origenLine2.value = oa.line2
    destinoLine1.value = da.line1
    destinoLine2.value = da.line2
  } catch {
    origenLine1.value = ''
    origenLine2.value = ''
    destinoLine1.value = ''
    destinoLine2.value = ''
  } finally {
    cargando.value = false
  }
}

watch(() => [props.origen, props.destino], geocodificar, { immediate: true })

const link = computed(() => {
  if (!props.origen || !props.destino) return null
  return buildUberLink(
    { ...props.origen, name: origenLine1.value, address: origenLine2.value },
    { ...props.destino, name: destinoLine1.value, address: destinoLine2.value },
  )
})
</script>

<style scoped>
.uber-card {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 14px;
  margin-top: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif;
}

.uber-header {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 12px;
}

.uber-brand {
  font-size: 15px;
  font-weight: 700;
  color: var(--label-primary);
  font-family: monospace;
  letter-spacing: -0.5px;
}

.uber-subtitle {
  font-size: 12px;
  color: rgba(60, 60, 67, 0.45);
}

.uber-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(60, 60, 67, 0.5);
  margin-bottom: 10px;
}

.uber-field-group {
  margin-bottom: 10px;
}

.uber-field-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(60, 60, 67, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 5px;
}

.uber-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.uber-dot--origin { background: var(--success); }
.uber-dot--dest   { background: var(--danger); }

.uber-input {
  width: 100%;
  height: 34px;
  background: var(--bg-grouped);
  border: none;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: var(--label-primary);
  outline: none;
  font-family: inherit;
  margin-bottom: 5px;
  box-sizing: border-box;
  -webkit-appearance: none;
}

.uber-input:last-of-type { margin-bottom: 0; }
.uber-input::placeholder { color: rgba(60, 60, 67, 0.32); }
.uber-input:focus { background: var(--bg-secondary); }

.uber-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 40px;
  margin-top: 12px;
  border: 1.5px solid rgba(60, 60, 67, 0.2);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--label-primary);
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
  box-sizing: border-box;
}

.uber-link:active {
  background: rgba(0, 0, 0, 0.04);
}
</style>
